// src/encounter/lib/apply-vaccination-administration.ts
// Nurse "Give" save (PROD-1227): merge an administered VaccinationDraft into the loaded
// PatientAssessment by REPLACING the matching nested entry, then the caller PUTs the whole doc
// (useSaveAssessment). The lot/expiry the nurse picks ride the saved vaccination → on sign-lock the
// producer (PROD-1221) emits them → Inventory deducts that exact lot.
//
// We replace in-doc (not via the canvas autosave) so the save is explicit + decoupled from the
// canvas content-snapshot. The vaccination round-trip (from-/to-patient-assessment) independently
// protects this write from being wiped by a later canvas autosave. PURE — no I/O.
import type { AssessmentPlanVaccination, PatientAssessment } from '@/types/assessment-api';
import type { VaccinationDraft } from '../vaccination-admin-editor';
import { vaccinationDraftToEntry } from './to-vaccination-entry';

/** Locate the nested vaccination whose identifier matches `requestId` (the view-model id). */
export function findVaccinationEntry(
  doc: PatientAssessment | null | undefined,
  requestId: string,
): AssessmentPlanVaccination | null {
  for (const dx of doc?.assessmentPlanCondition ?? []) {
    const hit = (dx.childMedicalCode?.assessmentPlanVaccination ?? []).find(
      (v) => (v.identifier ?? '') === requestId,
    );
    if (hit) return hit;
  }
  return null;
}

export interface ApplyVaccinationContext {
  encounter: string;
  subject: string; // patientId
  performer: string; // provider userId
  /**
   * TRUE when a nurse is RECORDING an administration (the "Give" path); FALSE when a doctor is
   * amending a plan request ("Edit"). Required, not optional, because getting it wrong is a
   * patient-safety defect in one direction and a lost request in the other — a caller must choose.
   *
   * When true the fulfilled request is CLEARED (`isAdministrationRequested: false`), which is what
   * Flutter does on the same save (pre_encounter_widget.dart:1491-1495). Without it the dose stays
   * on "Give" forever and can be administered and recorded a second time.
   */
  administered: boolean;
}

/**
 * Replace the request's nested entry with the administered one (draft → entry, preserving the
 * existing identity: identifier/parentId/assessmentPlanId/orderId). Returns a NEW doc, or null when
 * the request can't be located by identifier (caller keeps the editor open). Matching is by the
 * server identifier — a request the nurse can act on has been persisted, so it has one.
 */
export function applyVaccinationAdministration(
  doc: PatientAssessment,
  requestId: string,
  draft: VaccinationDraft,
  ctx: ApplyVaccinationContext,
): PatientAssessment | null {
  const conditions = doc.assessmentPlanCondition;
  if (!conditions || conditions.length === 0) return null;

  let found = false;
  const nextConditions = conditions.map((dx) => {
    const vax = dx.childMedicalCode?.assessmentPlanVaccination;
    if (!vax || vax.length === 0) return dx;
    const idx = vax.findIndex((v) => (v.identifier ?? '') === requestId);
    if (idx < 0) return dx;

    found = true;
    const existing = vax[idx];
    const entry = vaccinationDraftToEntry(draft, {
      encounter: ctx.encounter,
      subject: ctx.subject,
      performer: ctx.performer,
      existing: {
        identifier: existing.identifier,
        parentId: existing.parentId,
        assessmentPlanId: existing.assessmentPlanId,
        orderId: existing.orderId,
      },
    });
    const nextVax = [...vax];
    // CLEAR THE FULFILLED REQUEST (PROD-1801, 30 Jul). The draft carries `isAdministrationRequested`
    // straight through — the editor hides that switch in administration mode, so the nurse cannot turn
    // it off and it round-tripped as `true`. Flutter sets it false on this exact save. Leaving it true
    // left the given dose reading "Give", so it could be administered and recorded twice.
    nextVax[idx] = ctx.administered ? { ...entry, isAdministrationRequested: false } : entry;
    return {
      ...dx,
      childMedicalCode: { ...dx.childMedicalCode!, assessmentPlanVaccination: nextVax },
    };
  });

  if (!found) return null;
  return { ...doc, assessmentPlanCondition: nextConditions };
}
